import type { IFavoriteApiService, IProductEntityFavorite } from '../models';

import { FavoriteProduct } from './FavoriteProduct';

export class _FavoritesStore {
  public favoriteProducts = ref<FavoriteProduct[]>([]);
  public canFetchMore = ref(true);
  public totalCount = ref<number>(0);

  #lastFetchedPage = 0;
  #fetchPromise?: Promise<void>;

  #ApiService?: IFavoriteApiService;

  public setApiService(apiService: IFavoriteApiService) {
    this.#ApiService = apiService;
  }

  public reset() {
    this.favoriteProducts.value = [];
    this.#lastFetchedPage = 0;
    this.canFetchMore.value = true;
    this.totalCount.value = 0;
  }

  public async fetchNextFavoritesPage() {
    if (!this.canFetchMore.value) return this.favoriteProducts.value;

    return this.fetchFavorites(this.#lastFetchedPage + 1);
  }

  public async fetchFavorites(page: number) {
    if (this.#fetchPromise) return this.#fetchPromise;

    this.#fetchPromise = this.#ApiService
      ?.getFavorites(page)
      .then((response) => {
        /** handle pagination */
        this.#lastFetchedPage += 1;
        this.canFetchMore.value = response.pagination.hasMore;
        this.totalCount.value = response.pagination.total;

        const items = response.items;
        if (Array.isArray(items)) {
          const products = items.map(
            (favoriteProductFromApi) => new FavoriteProduct({ favoriteProductFromApi })
          );
          this.favoriteProducts.value.push(...products);
        }
      })
      .catch(() => {
        this.canFetchMore.value = false;
      })
      .finally(() => {
        this.#fetchPromise = undefined;
      });

    return this.#fetchPromise;
  }

  public async addFavorite(product: IProductEntityFavorite) {
    if (!product.id) return;

    product.isFavorite = true;

    return this.#ApiService
      ?.addFavorite(product.id)
      .then(() => {
        if (!this.getFavoriteProductById(product.id)) {
          this.favoriteProducts.value.unshift(new FavoriteProduct({ product }));
          this.totalCount.value += 1;
        }
      })
      .catch(() => {
        product.isFavorite = false;
      });
  }

  public async removeFavorite(product: IProductEntityFavorite) {
    product.isFavorite = false;

    const favoriteProduct = this.getFavoriteProductById(product.id);
    favoriteProduct?.setRemovingState(true);

    return this.#ApiService
      ?.removeFavorite(product.id)
      .then(() => {
        this.favoriteProducts.value = this.favoriteProducts.value.filter(
          (it) => it.product.id !== product.id
        );
        this.totalCount.value -= 1;
      })
      .catch(() => {
        product.isFavorite = true;
      })
      .finally(() => {
        favoriteProduct?.setRemovingState(false);
      });
  }

  private getFavoriteProductById(productId: string) {
    return this.favoriteProducts.value.find((it) => it.product.id === productId);
  }

  public async syncFavorites() {
    return this.#ApiService?.syncFavorites();
  }
}
